import type { AuthV1 } from '@alpod-ai-writer/backend';

import { ApiError } from './apiError';

const REFRESH_TOKENS_BEFORE_EXPIRATION_MS = 60 * 1000 * 5; // 5 minutes
const MAX_REFRESH_RETRIES = 3;

export interface ApiClientOptions {
    baseUrl: string;
}

type ErrorBody = {
    error: string,
    message: string,
    details?: unknown,
    reqId?: string,
    traceId?: string
};

interface RawRequestOptions {
    params?: Record<string, unknown>;
    query?: Record<string, unknown>;
    body?: unknown;
    headers?: Record<string, string>;
    transformResponse?: (response: any) => unknown;
}

export interface RequestOptions<Response> {
    params?: Record<string, unknown>;
    query?: Record<string, unknown>;
    transformResponse?: (response: Response) => Response;
}

export interface RequestOptionsWithBody<Body, Response> extends RequestOptions<Response> {
    body: Body;
}

export class ApiClient extends EventTarget {
    private refreshPromise: Promise<AuthV1.Tokens | null> | null = null;
    private _needToRefreshTokens: boolean = false;

    /**
     * Access token lives in memory only — never persisted. The refresh token is
     * stored by the backend as an httpOnly, SameSite=Strict cookie, so a page
     * reload recovers the session via `tryRefreshTokens()` without exposing the
     * access token to XSS.
     */
    private _session: AuthV1.Tokens | null = null;

    public constructor(public readonly opts: ApiClientOptions) {
        super();
    }

    public destroy(): void {
        // no-op — retained for API compatibility
    }

    private storeSession(session: AuthV1.Tokens): void {
        this._session = session;
    }

    public async establishSession(newSession: AuthV1.Tokens): Promise<void> {
        this._session = newSession;
        this._needToRefreshTokens = false;

        this.dispatchEvent(new Event('sessionEstablished'));
    }

    private getSession(): AuthV1.Tokens | null {
        return this._session;
    }

    private clearSession(): void {
        this._session = null;
    }

    public getIdentityId(): string | null {
        return this._session?.identityId ?? null;
    }

    public getSessionId(): string | null {
        return this._session?.sessionId ?? null;
    }

    public hasSession(): boolean {
        return this._session !== null;
    }

    public deleteSession(): void {
        this.clearSession();
        this.dispatchEvent(new Event('sessionDeleted'));
    }

    private getRequestParams(path: string, opts: RawRequestOptions) {
        let _path = path;
        let _body: FormData | string | undefined;
        const _headers = opts.headers ?? {};

        if (opts.params) {
            for (const [key, value] of Object.entries(opts.params)) {
                _path = _path.replace(`:${key}`, String(value));
            }
        }

        const _url = new URL(
            this.opts.baseUrl + _path,
            !this.opts.baseUrl.startsWith('http') ? window.location.origin : undefined
        );

        if (opts.query) {
            for (const [key, value] of Object.entries(opts.query)) {
                let _value: string;

                if (value instanceof Date) {
                    _value = value.toISOString();
                } else if (value instanceof Object) {
                    _value = JSON.stringify(value);
                } else {
                    _value = String(value);
                }

                _url.searchParams.append(key, _value);
            }
        }

        if (opts.body instanceof FormData) {
            _body = opts.body;
        } else if (opts.body !== undefined && opts.body !== null) {
            _body = JSON.stringify(opts.body);
            _headers['Content-Type'] = 'application/json';
        }

        return { url: _url, body: _body, headers: _headers };
    }

    private async makeRequest<Response = unknown>(method: string, path: string, opts: RawRequestOptions): Promise<Response> {
        const { url, body, headers } = this.getRequestParams(path, opts);

        const res = await fetch(url, { method, body, headers });

        if (!res.ok) {
            let body: unknown;
            try {
                body = await res.json();
            } catch {
                // ignore parse error
            }

            let errorBody: undefined | ErrorBody;
            if (body instanceof Object && 'error' in body) {
                errorBody = body as ErrorBody;
            }

            throw new ApiError(
                res.status,
                errorBody?.error ?? 'UnknownError',
                errorBody?.message ?? 'No message was provided.',
                errorBody?.details,
                errorBody?.reqId,
                errorBody?.traceId
            );
        } else {
            if (res.status === 204) {
                return null as unknown as Response;
            }

            const text = await res.text();
            if (!text) {
                return null as unknown as Response;
            }

            const data = JSON.parse(text);
            return opts.transformResponse ? opts.transformResponse(data) as Response : data;
        }
    }

    /**
     * Attempt to recover a session on app startup by exchanging the httpOnly
     * refresh cookie for an access token. Returns `null` if no valid refresh
     * cookie exists (user not logged in) — never throws for routine "no session".
     */
    public async bootstrapSession(): Promise<AuthV1.Tokens | null> {
        try {
            return await this.refreshTokens();
        } catch {
            this._session = null;
            return null;
        }
    }

    public async refreshTokens(): Promise<AuthV1.Tokens> {
        const res = await this.makeRequest<AuthV1.Tokens>('POST', '/v1/auth/token/refreshByCookie', {});
        const session: AuthV1.Tokens = {
            ...res,
            accessTokenExpiresAt: new Date(res.accessTokenExpiresAt)
        };

        this.storeSession(session);
        this._needToRefreshTokens = false;
        this.dispatchEvent(new Event('tokenUpdated'));

        return session;
    }

    private isTransientError(err: unknown): boolean {
        if (err instanceof TypeError && err.message.includes('fetch')) {
            return true;
        }
        if (err instanceof ApiError && err.status >= 500) {
            return true;
        }
        return false;
    }

    public async tryRefreshTokens(): Promise<AuthV1.Tokens | null> {
        let refreshRetries = 0;

        while (true) {
            try {
                return await this.refreshTokens();
            } catch (err) {
                if (this.isTransientError(err)) {
                    await new Promise(resolve => setTimeout(resolve, Math.min(1000 * Math.pow(2, refreshRetries), 30000)));
                    refreshRetries++;
                    continue;
                }

                if (err instanceof ApiError && err.status === 401) {
                    refreshRetries++;
                    if (refreshRetries >= MAX_REFRESH_RETRIES) {
                        break;
                    }
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    continue;
                }

                if (err instanceof ApiError && (err.status === 422 || err.status === 400 || err.status === 403)) {
                    break;
                }

                break;
            }
        }

        this.clearSession();
        this.dispatchEvent(new Event('sessionExpired'));
        throw new Error('Session expired.');
    }

    private async makeRequestWithTokens<Response = unknown>(method: string, path: string, opts: RawRequestOptions): Promise<Response> {
        let session = this.getSession();

        if (session) {
            const tokenExpiresInMs = session.accessTokenExpiresAt.getTime() - Date.now();

            if (tokenExpiresInMs < REFRESH_TOKENS_BEFORE_EXPIRATION_MS || this._needToRefreshTokens) {
                if (this.refreshPromise) {
                    session = await this.refreshPromise;
                } else {
                    try {
                        this.refreshPromise = this.tryRefreshTokens();
                        session = await this.refreshPromise;
                    } finally {
                        this.refreshPromise = null;
                    }
                }
            }
        }

        try {
            return await this.makeRequest<Response>(method, path, {
                ...opts,
                headers: {
                    ...opts.headers,
                    authorization: session ? `Bearer ${session.accessToken}` : ''
                }
            });
        } catch (err) {
            if (err instanceof ApiError && err.status === 401) {
                this._needToRefreshTokens = true;
            }
            throw err;
        }
    }

    public async get<Response>(path: string, opts?: RequestOptions<Response>): Promise<Response> {
        return this.makeRequestWithTokens<Response>('GET', path, { ...opts });
    }

    public async post<Body, Response>(path: string, opts: RequestOptionsWithBody<Body, Response>): Promise<Response> {
        return this.makeRequestWithTokens<Response>('POST', path, { ...opts });
    }

    public async put<Body, Response>(path: string, opts: RequestOptionsWithBody<Body, Response>): Promise<Response> {
        return this.makeRequestWithTokens<Response>('PUT', path, { ...opts });
    }

    public async delete<Response>(path: string, opts: RequestOptions<Response>): Promise<Response> {
        return this.makeRequestWithTokens<Response>('DELETE', path, { ...opts });
    }

    public async deleteWithBody<Body, Response>(path: string, opts: RequestOptionsWithBody<Body, Response>): Promise<Response> {
        return this.makeRequestWithTokens<Response>('DELETE', path, { ...opts });
    }
}
